import {
  DashboardOutlined,
  ShopOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  DollarOutlined,
  StarOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  BarChartOutlined,
  CarOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  CustomerServiceOutlined,
  AuditOutlined,
} from '@ant-design/icons';

// Role definitions
export const ROLES = {
  ADMIN: 1,
  STATISTIKA: 5,
  PROKURATURA: 6,
  SOLIQ: 7,
  KONSERT: 8,
  TRANSPORT: 9,
  YOL_HARAKATI: 10,
};

export const ROLE_NAMES = {
  [ROLES.ADMIN]: 'Administrator',
  [ROLES.STATISTIKA]: 'Statistika Boshqarmasi',
  [ROLES.PROKURATURA]: 'Prokuratura',
  [ROLES.SOLIQ]: 'Soliq',
  [ROLES.KONSERT]: "O'zbek konsert",
  [ROLES.TRANSPORT]: 'Transport Boshqarmasi',
  [ROLES.YOL_HARAKATI]: "Yo'l harakati boshqarmasi",
};

// All available routes
export const ALL_ROUTES = [
  '/',
  '/providers',
  '/services',
  '/bookings',
  '/payments',
  '/reports',
  '/reviews',
  '/regions',
  '/tax-analysis',
];

// Which routes each role can access
export const ROLE_ROUTES = {
  [ROLES.ADMIN]: ALL_ROUTES,
  [ROLES.STATISTIKA]: ['/', '/providers', '/services', '/bookings', '/payments', '/reviews', '/regions'],
  [ROLES.PROKURATURA]: ['/', '/reports', '/providers', '/bookings', '/payments'],
  [ROLES.SOLIQ]: ['/', '/payments', '/providers', '/services', '/reports', '/tax-analysis'],
  [ROLES.KONSERT]: ['/', '/services', '/bookings', '/reviews', '/providers'],
  [ROLES.TRANSPORT]: ['/', '/providers', '/regions', '/services'],
  [ROLES.YOL_HARAKATI]: ['/', '/providers', '/regions', '/services'],
};

// Menu structure per role
// Admin gets everything flat + grouped dropdowns
// Other roles get their allowed pages, some grouped into dropdowns

const allMenuItems = {
  dashboard: { key: '/', icon: DashboardOutlined, label: 'Dashboard' },
  providers: { key: '/providers', icon: ShopOutlined, label: 'Provayderlar' },
  services: { key: '/services', icon: AppstoreOutlined, label: 'Xizmatlar' },
  bookings: { key: '/bookings', icon: CalendarOutlined, label: 'Buyurtmalar' },
  payments: { key: '/payments', icon: DollarOutlined, label: "To'lovlar" },
  reports: { key: '/reports', icon: FileTextOutlined, label: 'Shikoyatlar' },
  reviews: { key: '/reviews', icon: StarOutlined, label: 'Sharhlar' },
  regions: { key: '/regions', icon: EnvironmentOutlined, label: 'Hududlar' },
  taxAnalysis: { key: '/tax-analysis', icon: AuditOutlined, label: 'Soliq tahlili' },
};

const makeItem = (id, prefix) => {
  const item = allMenuItems[id];
  return { key: prefix ? `${prefix}:${item.key}` : item.key, icon: item.icon, label: item.label };
};

const makeGroup = (key, icon, label, children) => ({
  key,
  icon,
  label,
  children,
});

// Build menu for each role
export const getRoleMenuItems = (roleId) => {
  switch (roleId) {
    case ROLES.ADMIN:
      return [
        makeItem('dashboard'),
        makeItem('providers'),
        makeItem('services'),
        makeItem('bookings'),
        makeItem('payments'),
        makeItem('reports'),
        makeItem('reviews'),
        makeItem('regions'),
        { type: 'divider' },
        makeGroup('dept_statistika', BarChartOutlined, 'Statistika Boshqarmasi', [
          makeItem('providers', 'stat'),
          makeItem('services', 'stat'),
          makeItem('regions', 'stat'),
          makeItem('bookings', 'stat'),
          makeItem('payments', 'stat'),
          makeItem('reviews', 'stat'),
        ]),
        makeGroup('dept_prokuratura', SafetyCertificateOutlined, 'Prokuratura', [
          makeItem('reports', 'prok'),
          makeItem('providers', 'prok'),
          makeItem('bookings', 'prok'),
          makeItem('payments', 'prok'),
        ]),
        makeGroup('dept_soliq', BankOutlined, 'Soliq', [
          makeItem('taxAnalysis', 'soliq'),
          makeItem('payments', 'soliq'),
          makeItem('providers', 'soliq'),
          makeItem('services', 'soliq'),
          makeItem('reports', 'soliq'),
        ]),
        makeGroup('dept_konsert', CustomerServiceOutlined, "O'zbek konsert", [
          makeItem('services', 'kons'),
          makeItem('bookings', 'kons'),
          makeItem('reviews', 'kons'),
          makeItem('providers', 'kons'),
        ]),
        makeGroup('dept_transport', CarOutlined, 'Transport Boshqarmasi', [
          makeItem('providers', 'trans'),
          makeItem('services', 'trans'),
          makeItem('regions', 'trans'),
        ]),
        makeGroup('dept_yol', CarOutlined, "Yo'l harakati boshqarmasi", [
          makeItem('providers', 'yol'),
          makeItem('services', 'yol'),
          makeItem('regions', 'yol'),
        ]),
      ];

    case ROLES.STATISTIKA:
      return [
        makeItem('dashboard'),
        makeGroup('data', BarChartOutlined, "Ma'lumotlar", [
          makeItem('providers'),
          makeItem('services'),
          makeItem('regions'),
        ]),
        makeGroup('operations', CalendarOutlined, 'Operatsiyalar', [
          makeItem('bookings'),
          makeItem('payments'),
          makeItem('reviews'),
        ]),
      ];

    case ROLES.PROKURATURA:
      return [
        makeItem('dashboard'),
        makeItem('reports'),
        makeGroup('tekshiruv', SafetyCertificateOutlined, 'Tekshiruv', [
          makeItem('providers'),
          makeItem('bookings'),
          makeItem('payments'),
        ]),
      ];

    case ROLES.SOLIQ:
      return [
        makeItem('dashboard'),
        makeItem('taxAnalysis'),
        makeItem('payments'),
        makeGroup('soliq_data', BankOutlined, "Soliq ma'lumotlari", [
          makeItem('providers'),
          makeItem('services'),
          makeItem('reports'),
        ]),
      ];

    case ROLES.KONSERT:
      return [
        makeItem('dashboard'),
        makeGroup('konsert', CustomerServiceOutlined, 'Konsert boshqaruvi', [
          makeItem('services'),
          makeItem('bookings'),
          makeItem('reviews'),
        ]),
        makeItem('providers'),
      ];

    case ROLES.TRANSPORT:
      return [
        makeItem('dashboard'),
        makeGroup('transport', CarOutlined, 'Transport', [
          makeItem('providers'),
          makeItem('services'),
          makeItem('regions'),
        ]),
      ];

    case ROLES.YOL_HARAKATI:
      return [
        makeItem('dashboard'),
        makeGroup('yol', CarOutlined, "Yo'l harakati", [
          makeItem('providers'),
          makeItem('services'),
          makeItem('regions'),
        ]),
      ];

    default:
      return [makeItem('dashboard')];
  }
};

// Check if a role has access to a route
export const hasRouteAccess = (roleId, route) => {
  const routes = ROLE_ROUTES[roleId];
  if (!routes) return false;
  return routes.includes(route);
};
